// src/pages/HomePage.jsx
import React, { useState, useEffect, useRef, useContext, useTransition } from 'react';
import { AppContext } from './AppContext';
import { Percent, Search, Landmark, MapPin, Handshake, Map } from 'lucide-react';
import ProjectCards from './ProjectCards';
import Footer from './Footer';
import Header from './Header';

const TAGS = ['New Launch', 'Top Rated', 'Ultra Luxury', 'Affordable'];

const features = [
    { Icon: Percent, title: 'Zero Brokerage, Full Transparency', description: 'Pay no brokerage—ever. We work for your best interest, not commissions.' },
    { Icon: Search, title: 'Tailored Property Recommendations', description: 'Discover homes that match your needs, budget, and lifestyle perfectly.' },
    { Icon: Landmark, title: 'Loan & Documentation Assistance', description: 'Get end-to-end help with home loans, paperwork, and approvals.' },
    { Icon: MapPin, title: 'Expert-Guided Site Visits', description: 'Visit shortlisted properties with experienced professionals by your side.' },
    { Icon: Handshake, title: 'Support Through Possession & Beyond', description: 'From booking to key handover and post-move questions—we’re with you all the way.' },
    { Icon: Map, title: 'Local Insights & Verified Listings', description: 'Access genuine listings with neighborhood insights you can trust.' },
];

export default function HomePage() {
    const { settings, projectHighlights, developersData } = useContext(AppContext);
    const [trendingHtml, setTrendingHtml] = useState('');
    const trendingRef = useRef(null);
    const [trendingTag, setTrendingTag] = useState(TAGS[0]);
    const [isPending, startTransition] = useTransition();


    // Prepare developers array from context
    const devArray = developersData?.developers
        ? [...developersData.developers].sort((a, b) => Number(a.sequence) - Number(b.sequence))
        : [];

    // Load trending HTML
    useEffect(() => {
        if (projectHighlights) {
            const key = trendingTag.replace(/ /g, '');
            setTrendingHtml(projectHighlights[key] || '');
        }
    }, [projectHighlights, trendingTag]);

    // Re-init Flickity
    useEffect(() => {
        if (window.Flickity && trendingRef.current) {
            new window.Flickity(trendingRef.current, { cellAlign: 'left', contain: true });
        }
    }, [trendingHtml]);

    const waMessage = settings?.whatsapp_default_message || '';

    return (
        <div style={{ fontFamily: "'Rubik', sans-serif" }}>

            <Header />

            {/* Banner */}
            <section className="bg-yellow-400 text-black text-center py-20 px-4 md:px-8">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">Find Your Dream Home with PropXplorer</h2>
                <p className="text-base md:text-lg">Explore the best real estate projects in Kolkata</p>
                <a
                    href={`https://wa.me/919831577138?text=${encodeURIComponent(waMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-block bg-black text-yellow-400 hover:bg-yellow-400 hover:text-black transition border border-2 border-black font-semibold py-3 px-8 rounded-full text-sm md:text-base"
                >
                    Book Appointment
                </a>
            </section>

            {/* Featured Projects */}
            <div className="bg-[#f5f3f1] px-2 lg:px-6 p-8 lg:p-6" data-aos="fade-up">
                <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center lg:pt-4 animate-fade-in">Featured Projects</h2>
                <ProjectCards projectType="Residential" projectTag="Featured" />
            </div>

            {/* Trending Projects */}
            <div className="w-full max-w-screen-xl mx-auto bg-yellow-400 px-2 lg:px-6 p-8 lg:p-6">
                <h2
                    className="text-2xl font-bold text-gray-800 mb-8 text-center lg:pt-4 animate-fade-in"
                    data-aos="fade-up"
                >
                    Trending Projects
                </h2>

                {/* Filter Buttons */}
                <div className="flex gap-3 mb-6 flex-wrap justify-center">
                    {TAGS.map(tag => (
                        <button
                            key={tag}
                            onClick={() => {
                                // mark this update as low-priority so UI stays snappy
                                startTransition(() => {
                                    setTrendingTag(tag);
                                });
                            }}
                            disabled={isPending}
                            className={`
              px-4 py-2 rounded-full border border-gray-600 text-sm transition
              ${tag === trendingTag
                                    ? 'bg-black text-white font-semibold'
                                    : 'bg-white text-gray-800 hover:bg-black hover:text-white'
                                }
              ${isPending && tag === trendingTag ? 'opacity-50 cursor-wait' : ''}
            `}
                        >
                            {tag}
                        </button>
                    ))}
                </div>

                {/* Slider */}
                <div data-aos="fade-up">
                    <ProjectCards
                        projectType="Residential"
                        projectTag={trendingTag}
                    />
                </div>
            </div>


            {/* Our Partners */}
            <section className="relative w-full overflow-hidden" data-aos="zoom-in">
                <div className="border-t border-b border-gray-300 py-4 text-center">
                    <h3 className="text-2xl font-semibold text-gray-600">OUR PARTNERS</h3>
                </div>
                <div className="py-2 md:py-4 flex items-center animate-marquee whitespace-nowrap">
                    {devArray.map(dev => (
                        <div key={dev.slug} className="mx-2 flex-shrink-0">
                            <img
                                src={dev.logo || '/fallback.jpg'}
                                alt={dev.name}
                                className="w-[110px] h-[55px] md:w-[220px] md:h-[110px] object-contain"
                            />
                        </div>
                    ))}
                </div>
            </section>

            {/* Why PropXplorer */}
            <section className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <h2
                    className="text-3xl font-bold text-center text-gray-800 mb-12 bg-gradient-to-r from-blue-400 to-pink-500 bg-clip-text text-transparent"
                    data-aos="fade-up"
                >
                    Why PropXplorer
                </h2>
                <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {features.map(({ Icon, title, description }, i) => (
                        <div
                            key={title}
                            className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-center hover:shadow-lg hover:border-orange-400 transform hover:-translate-y-1 transition-all duration-300"
                            data-aos="fade-up"
                            data-aos-delay={i * 100}
                        >
                            <Icon className="block mx-auto text-orange-500 text-4xl mb-4" />
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
                        </div>
                    ))}
                </div>
            </section>
            <Footer />
        </div>
    );
}
